// client/src/pages/admin/CostReports.tsx
import React, { useState } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Table,
  Space,
  Typography,
  Progress,
  Tag,
  Tabs,
  DatePicker,
  Spin,
  Modal,
  Form,
  Input,
  InputNumber,
  Select
} from 'antd';
import {
  DollarOutlined,
  ThunderboltOutlined,
  ToolOutlined,
  UserOutlined,
  PieChartOutlined,
  DownloadOutlined,
  RiseOutlined,
  FallOutlined,
  PlusOutlined,
  CalculatorOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { costService, type BudgetUtilization, type CostBreakdown } from '../../services/costService';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const AdminCostReports: React.FC = () => {
  const queryClient = useQueryClient();
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>([
    dayjs().subtract(30, 'days'),
    dayjs()
  ]);
  const [activeTab, setActiveTab] = useState('overview');
  const [budgetModalVisible, setBudgetModalVisible] = useState(false);
  const [budgetForm] = Form.useForm();

  // Fetch cost report
  const { data: costReport, isLoading: reportLoading } = useQuery({
    queryKey: ['costReport', dateRange],
    queryFn: () => {
      const start = dateRange?.[0]?.toISOString();
      const end = dateRange?.[1]?.toISOString();
      return costService.getCostReport(start, end);
    },
  });

  // Fetch budget utilization
  const currentYear = dayjs().year();
  const currentMonth = dayjs().month() + 1;
  const { data: budgetUtilization, isLoading: budgetLoading } = useQuery({
    queryKey: ['budgetUtilization', currentYear, currentMonth],
    queryFn: () => costService.getBudgetUtilization(currentYear, currentMonth),
  });

  // Create budget mutation
  const createBudgetMutation = useMutation({
    mutationFn: (values: any) => costService.createBudget(values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budgetUtilization'] });
      queryClient.invalidateQueries({ queryKey: ['costReport'] });
      setBudgetModalVisible(false);
      budgetForm.resetFields();
    },
  });

  const handleExport = (format: 'pdf' | 'excel') => {
    // TODO: Implement export functionality
    console.log(`Exporting report as ${format}`);
  };

  const handleBudgetSave = async (values: any) => {
    createBudgetMutation.mutate(values);
  };

  const getUtilizationColor = (percentage: number) => {
    if (percentage >= 100) return '#ff4d4f';
    if (percentage >= 80) return '#fa8c16';
    return '#52c41a';
  };

  const getCategoryIcon = (category: string) => {
    const lowerCategory = category.toLowerCase();
    if (lowerCategory.includes('fuel') || lowerCategory.includes('carburant')) return <ThunderboltOutlined />;
    if (lowerCategory.includes('maintenance')) return <ToolOutlined />;
    if (lowerCategory.includes('salary') || lowerCategory.includes('salaire')) return <UserOutlined />;
    return <DollarOutlined />;
  };

  const categoryColumns: ColumnsType<CostBreakdown> = [
    {
      title: 'Catégorie',
      dataIndex: 'category',
      key: 'category',
      render: (category: string) => (
        <Space>
          {getCategoryIcon(category)}
          <strong>{category}</strong>
        </Space>
      ),
    },
    {
      title: 'Montant total',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount: number) => (
        <div style={{ fontWeight: 'bold' }}>
          {amount.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
        </div>
      ),
      sorter: (a, b) => a.total_amount - b.total_amount,
    },
    {
      title: 'Transactions',
      dataIndex: 'transaction_count',
      key: 'transaction_count',
      render: (count: number) => <Tag color="blue">{count}</Tag>,
    },
    {
      title: 'Montant moyen',
      dataIndex: 'avg_amount',
      key: 'avg_amount',
      render: (amount: number) =>
        amount ? amount.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' }) : '-',
    },
    {
      title: 'Part du total',
      dataIndex: 'percentage',
      key: 'percentage',
      render: (percentage: number) => (
        <Progress
          percent={percentage}
          size="small"
          strokeColor="#1890ff"
        />
      ),
    },
  ];

  const budgetColumns: ColumnsType<BudgetUtilization> = [
    {
      title: 'Budget',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record) => (
        <Space>
          {getCategoryIcon(record.category)}
          <div>
            <div style={{ fontWeight: 'bold' }}>{name}</div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.category} - {record.period}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: 'Alloué',
      dataIndex: 'allocated_amount',
      key: 'allocated_amount',
      render: (amount: number) => (
        <div style={{ fontWeight: 'bold' }}>
          {amount.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
        </div>
      ),
    },
    {
      title: 'Dépensé',
      dataIndex: 'spent_amount',
      key: 'spent_amount',
      render: (amount: number) =>
        amount.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' }),
    },
    {
      title: 'Restant',
      dataIndex: 'remaining_amount',
      key: 'remaining_amount',
      render: (amount: number) => (
        <div style={{ color: amount >= 0 ? '#52c41a' : '#ff4d4f', fontWeight: 'bold' }}>
          {amount.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
        </div>
      ),
    },
    {
      title: 'Utilisation',
      key: 'utilization',
      render: (_, record) => (
        <div>
          <Progress
            percent={record.utilization_percentage}
            size="small"
            strokeColor={getUtilizationColor(record.utilization_percentage)}
          />
          <Tag
            color={
              record.status === 'over_budget' ? 'red' :
              record.status === 'alert' ? 'orange' : 'green'
            }
            style={{ marginTop: '4px' }}
          >
            {record.status === 'over_budget' ? 'Dépassé' :
             record.status === 'alert' ? 'Alerte' : 'OK'}
          </Tag>
        </div>
      ),
    },
    {
      title: 'Transactions',
      dataIndex: 'total_transactions',
      key: 'total_transactions',
      render: (count: number) => <Tag color="blue">{count}</Tag>,
    },
  ];

  if (reportLoading || budgetLoading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const summary = costReport?.summary;
  const byCategory = costReport?.by_category || [];
  const budgets = budgetUtilization || [];

  // Calculate budget totals
  const totalAllocated = budgets.reduce((sum: number, b: BudgetUtilization) => sum + b.allocated_amount, 0);
  const totalSpent = budgets.reduce((sum: number, b: BudgetUtilization) => sum + b.spent_amount, 0);
  const totalRemaining = totalAllocated - totalSpent;
  const overallUtilization = totalAllocated > 0 ? (totalSpent / totalAllocated) * 100 : 0;

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <Card style={{ marginBottom: '24px' }}>
        <Row align="middle" justify="space-between">
          <Col>
            <Title level={2} style={{ margin: 0 }}>
              <CalculatorOutlined style={{ marginRight: '8px' }} />
              Rapports de Coûts
            </Title>
            <Text type="secondary">
              {dateRange?.[0]?.format('DD/MM/YYYY')} - {dateRange?.[1]?.format('DD/MM/YYYY')}
            </Text>
          </Col>
          <Col>
            <Space>
              <RangePicker
                value={dateRange}
                onChange={(dates) => setDateRange(dates)}
                format="DD/MM/YYYY"
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setBudgetModalVisible(true)}
              >
                Créer Budget
              </Button>
              <Button icon={<DownloadOutlined />} onClick={() => handleExport('pdf')}>
                PDF
              </Button>
              <Button icon={<DownloadOutlined />} onClick={() => handleExport('excel')}>
                Excel
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Summary Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Coût Total"
              value={summary?.total_cost || 0}
              precision={2}
              suffix="€"
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
            <div style={{ marginTop: '8px' }}>
              <Text type="secondary">
                {summary?.total_transactions || 0} transactions
              </Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Budget Alloué"
              value={totalAllocated}
              precision={2}
              suffix="€"
              prefix={<PieChartOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
            <div style={{ marginTop: '8px' }}>
              <Text type="secondary">
                {budgets.length} budgets actifs
              </Text>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Restant"
              value={totalRemaining}
              precision={2}
              suffix="€"
              prefix={totalRemaining >= 0 ? <RiseOutlined /> : <FallOutlined />}
              valueStyle={{ color: totalRemaining >= 0 ? '#52c41a' : '#ff4d4f' }}
            />
            <div style={{ marginTop: '8px' }}>
              <Progress
                percent={overallUtilization}
                size="small"
                strokeColor={getUtilizationColor(overallUtilization)}
              />
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Coût Moyen"
              value={summary?.avg_transaction_amount || 0}
              precision={2}
              suffix="€"
              valueStyle={{ color: '#fa8c16' }}
            />
            <div style={{ marginTop: '8px' }}>
              <Text type="secondary">
                Par transaction
              </Text>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Tabs */}
      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <Tabs.TabPane
            tab={
              <span>
                <PieChartOutlined />
                Répartition par Catégorie
              </span>
            }
            key="overview"
          >
            <Table
              columns={categoryColumns}
              dataSource={byCategory}
              rowKey="category"
              pagination={false}
              size="small"
            />
          </Tabs.TabPane>

          <Tabs.TabPane
            tab={
              <span>
                <CalculatorOutlined />
                Budgets
              </span>
            }
            key="budgets"
          >
            <Table
              columns={budgetColumns}
              dataSource={budgets}
              rowKey="budget_id"
              pagination={false}
              size="small"
            />
          </Tabs.TabPane>
        </Tabs>
      </Card>

      {/* Budget Creation Modal */}
      <Modal
        title="Créer un Budget"
        open={budgetModalVisible}
        onCancel={() => {
          setBudgetModalVisible(false);
          budgetForm.resetFields();
        }}
        onOk={() => budgetForm.submit()}
        confirmLoading={createBudgetMutation.isPending}
      >
        <Form
          form={budgetForm}
          layout="vertical"
          onFinish={handleBudgetSave}
        >
          <Form.Item
            name="name"
            label="Nom du budget"
            rules={[{ required: true, message: 'Nom requis' }]}
          >
            <Input placeholder="Ex: Budget Carburant Janvier 2024" />
          </Form.Item>

          <Form.Item
            name="category"
            label="Catégorie"
            rules={[{ required: true, message: 'Catégorie requise' }]}
          >
            <Select placeholder="Sélectionner une catégorie">
              <Option value="fuel">Carburant</Option>
              <Option value="maintenance">Maintenance</Option>
              <Option value="salaries">Salaires</Option>
              <Option value="insurance">Assurance</Option>
              <Option value="other">Autre</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="period"
            label="Période"
            rules={[{ required: true, message: 'Période requise' }]}
          >
            <Select placeholder="Sélectionner la période">
              <Option value="monthly">Mensuel</Option>
              <Option value="quarterly">Trimestriel</Option>
              <Option value="yearly">Annuel</Option>
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="year"
                label="Année"
                rules={[{ required: true, message: 'Année requise' }]}
                initialValue={currentYear}
              >
                <InputNumber
                  min={2020}
                  max={currentYear + 5}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.period !== currentValues.period
                }
              >
                {({ getFieldValue }) =>
                  getFieldValue('period') === 'monthly' ? (
                    <Form.Item
                      name="month"
                      label="Mois"
                      rules={[{ required: true, message: 'Mois requis' }]}
                    >
                      <InputNumber
                        min={1}
                        max={12}
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  ) : getFieldValue('period') === 'quarterly' ? (
                    <Form.Item
                      name="quarter"
                      label="Trimestre"
                      rules={[{ required: true, message: 'Trimestre requis' }]}
                    >
                      <InputNumber
                        min={1}
                        max={4}
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  ) : null
                }
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="allocated_amount"
            label="Montant Alloué"
            rules={[{ required: true, message: 'Montant requis' }]}
          >
            <InputNumber
              min={0}
              precision={2}
              style={{ width: '100%' }}
              addonAfter="€"
            />
          </Form.Item>

          <Form.Item
            name="alert_threshold"
            label="Seuil d'Alerte (%)"
            initialValue={80}
          >
            <InputNumber
              min={0}
              max={100}
              style={{ width: '100%' }}
              addonAfter="%"
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminCostReports;
